import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { emptyApiSlice } from '../emptyApiSlice';
import {
  CreateOfferingRequest,
  GenerateQueriesRequest,
  IncludeOfferingRequest,
  Offering,
  OfferingResponse,
  OfferingSnapshot,
  ReportOfferingsRaw,
} from './offeringsApiTypes';

export interface OfferingsQueryResult {
  data: OfferingResponse[];
  statusCode?: number;
}

export const offeringsApiSlice = emptyApiSlice.injectEndpoints({
  endpoints: builder => ({
    getOfferings: builder.query<OfferingsQueryResult, string>({
      query: domainId => ({
        url: `domains/${domainId}/offerings`,
        method: 'GET',
        params: {
          'with-archived': true,
          'with-product-queries': true,
          'with-report-assocs': true,
          'with-domain-assocs': true,
          'with-geodata': true,
        },
      }),
      transformResponse: (
        raw: { data: OfferingResponse[] },
        meta: FetchBaseQueryMeta
      ): OfferingsQueryResult => ({
        data: raw.data,
        statusCode: meta?.response?.status,
      }),
      providesTags: ['Offerings'],
    }),
    createOffering: builder.mutation<void, CreateOfferingRequest>({
      query: ({ domainId, offering }) => ({
        url: `domains/${domainId}/offerings`,
        method: 'PUT',
        body: {
          data: offering,
        },
      }),
      invalidatesTags: ['Offerings'],
    }),
    getOfferingById: builder.query<Offering, string>({
      query: id => ({
        url: `/offerings/${id}`,
        method: 'GET',
      }),
      providesTags: ['Offerings'],
    }),
    updateOffering: builder.mutation<void, Offering>({
      query: offering => ({
        url: `/offerings/${offering.id}`,
        method: 'PUT',
        body: {
          data: offering,
        },
      }),
      invalidatesTags: ['Offerings'],
    }),
    deleteOffering: builder.mutation<void, string>({
      query: id => ({
        url: `/offerings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Offerings'],
    }),
    includeOffering: builder.mutation<void, IncludeOfferingRequest>({
      query: ({ domainId, offeringId }) => ({
        url: `/domains/${domainId}/offerings/${offeringId}/domain_assoc`,
        method: 'PATCH',
        body: {
          enabled: true,
        },
      }),
      invalidatesTags: ['Offerings'],
    }),
    excludeOffering: builder.mutation<void, IncludeOfferingRequest>({
      query: ({ domainId, offeringId }) => ({
        url: `/domains/${domainId}/offerings/${offeringId}/domain_assoc`,
        method: 'PATCH',
        body: {
          enabled: false,
        },
      }),
      invalidatesTags: ['Offerings'],
    }),
    getOfferingSnapshot: builder.query<
      OfferingSnapshot,
      { reportId: string; offeringId: string }
    >({
      query: ({ reportId }) => ({
        url: `reports/${reportId}/offerings`,
        method: 'GET',
        params: { 'with-product-queries': true },
      }),
      transformResponse: (
        raw: ReportOfferingsRaw,
        _meta,
        { offeringId }
      ): OfferingSnapshot => {
        const match = raw.data.find(o =>
          o.meta?.report_assocs?.data?.some(
            a => a.data.offering_id === offeringId
          )
        );
        return {
          name: match?.data.service_name ?? '',
          description: match?.data.description ?? '',
          targetAudience: match?.data.target_audience ?? '',
          geographicFocus: match?.data.geographic_focus ?? '',
          visibilityQueries:
            match?.data.product_queries?.data?.map(q => q.data.value) ?? [],
        };
      },
    }),
    archiveOffering: builder.mutation<void, string>({
      query: offeringId => ({
        url: `/offerings/${offeringId}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: ['Offerings'],
    }),
    restoreOffering: builder.mutation<void, string>({
      query: offeringId => ({
        url: `/offerings/${offeringId}/archive`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Offerings'],
    }),
    approveOffering: builder.mutation<void, string>({
      query: offeringId => ({
        url: `/offerings/${offeringId}/review`,
        method: 'PUT',
      }),
      invalidatesTags: ['Offerings'],
    }),
    revokeOfferingApproval: builder.mutation<void, string>({
      query: offeringId => ({
        url: `/offerings/${offeringId}/review`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Offerings'],
    }),
    generateQueries: builder.mutation<
      { queries: string[] },
      GenerateQueriesRequest
    >({
      query: ({ domainId, body }) => ({
        url: `/domains/${domainId}/queries/generate`,
        method: 'POST',
        body: body,
      }),
    }),
  }),
});

export const {
  useGetOfferingsQuery,
  useCreateOfferingMutation,
  useGetOfferingByIdQuery,
  useUpdateOfferingMutation,
  useDeleteOfferingMutation,
  useIncludeOfferingMutation,
  useExcludeOfferingMutation,
  useGetOfferingSnapshotQuery,
  useArchiveOfferingMutation,
  useRestoreOfferingMutation,
  useApproveOfferingMutation,
  useRevokeOfferingApprovalMutation,
  useGenerateQueriesMutation,
} = offeringsApiSlice;
